import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Star, ArrowDown } from "lucide-react";
import { IMAGES } from "@/data/salon";
import { useBooking } from "@/context/BookingContext";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.12 },
  }),
};

export const Hero = () => {
  const { openBooking } = useBooking();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden pt-32 pb-16">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center min-h-[80vh]">
        {/* Left: kinetic text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-pink" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-pink">Hair · Brows · Lashes · Facials · Waxing</span>
          </motion.div>

          <h1 data-testid="hero-title" className="font-serif text-ink leading-[0.95] tracking-tight text-4xl sm:text-7xl lg:text-[5.5rem]">
            <span className="text-mask"><motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">The art of</motion.span></span>
            <span className="text-mask"><motion.span custom={1} variants={line} initial="hidden" animate="show" className="block italic text-pink">considered</motion.span></span>
            <span className="text-mask"><motion.span custom={2} variants={line} initial="hidden" animate="show" className="block">hair & beauty.</motion.span></span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-md text-base sm:text-lg text-muted font-light leading-relaxed"
          >
            Precision brow artistry in Motherwell, plus dedicated hair rituals in Glasgow. From sculptural cuts to colour and smooth finishes, every look is tailored to you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-now-cta"
              onClick={() => openBooking()}
              className="group relative overflow-hidden rounded-full bg-ink px-8 py-4 text-xs uppercase tracking-[0.2em] text-cream"
            >
              <span className="relative z-10 group-hover:text-ink transition-colors duration-300">Reserve your ritual</span>
              <span className="absolute inset-0 origin-left scale-x-0 bg-pink transition-transform duration-400 group-hover:scale-x-100" />
            </button>
            <button
              data-testid="hero-explore-hair-cta"
              onClick={() => document.getElementById("hair-showcase")?.scrollIntoView({ behavior: "smooth" })}
              className="font-mono text-xs uppercase tracking-[0.2em] text-ink/70 hover:text-ink flex items-center gap-2"
            >
              Explore hair rituals <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
            className="mt-12 flex items-center gap-3"
          >
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-pink text-pink" />)}</div>
            <span className="font-mono text-xs text-muted tracking-wider">4.3 / 5 · 38 Treatwell reviews</span>
          </motion.div>
        </div>

        {/* Right: arched framed photo with parallax */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            className="relative h-[62vh] lg:h-[74vh] w-full overflow-hidden rounded-t-[999px] rounded-b-[32px] border border-pink/30"
          >
            <motion.img
              src={IMAGES.hairHero}
              alt="Professional hair styling at Fabrowz"
              style={{ y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
            />
            <div className="absolute left-1/2 top-8 -translate-x-1/2 whitespace-nowrap rounded-full border border-cream/30 bg-ink/45 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cream backdrop-blur-md" data-testid="hero-hair-badge">
              Now in Glasgow · Hair
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.3, duration: 0.6 }}
            className="absolute -left-4 bottom-16 bg-cream/85 backdrop-blur-md border border-pink/30 rounded-2xl px-5 py-4 shadow-lg"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-pink">Glasgow Hair Studios</p>
            <p className="font-serif text-2xl text-ink">Cuts · Colour · Styling</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
